import cl from 'classnames'
import styles from './ViewersTable.module.scss'
import {
  PaginationButtons,
  SelectTreeView,
  TableBodyContainer,
  TableColHeaderProps,
  TableContainer,
  TableHeader,
  TableHorizontalScrollControls,
  Text2
} from '../../../../components/shared'
import { LoadingSpinner } from '../../../../components'
import React, { useEffect, useRef, useState } from 'react'
import {
  STATISTICS_PAGE_STORE,
  STATISTICS_VIEWER_STORE,
  STATISTICS_VIEWERS_TABLE_STORE, StatsViewersTableSortProps
} from '../../../../store'
import { IconFilterEmpty, IconMinusCircle, IconPlusCircle } from '../../../../assets'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { getViewersTableFlexWidth, ViewersTableProps, viewersTableWidths } from './ViewersTable.types'
import { ViewersProjectsTableBodyLine } from './ViewersProjectTableBodyLine'
import { StatsTableColType } from '../../../../store/StatsBaseTableStore'

const ViewersTableComponent = (props: ViewersTableProps) => {
  const {
    className,
    style,
    [STATISTICS_VIEWERS_TABLE_STORE]: {
      isExpandedInteractionsColumn,
      setIsExpandedInteractionsColumn,
      isExpandedWatchTimeColumn,
      setIsExpandedWatchTimeColumn,
      tableSettings,
      searchResults,
      paginationInfo,
      onPageSelect,
      selectorColLabel,
      processing,
      legendEntityLabel,
      onColumnDragEnd,
      getFilterPropsByColLabel,
      getViewersTableData,
      isColumnMarked,
      getSortDirection,
      handleSortDirectionChange,
      addExpensesColLabel,
      isTotalWatchTimeColDisabled
    },
    [STATISTICS_VIEWER_STORE]: {
      period
    },
    [STATISTICS_PAGE_STORE]: {
      selectedStoreIds
    }
  } = props

  useEffect(getViewersTableData, [selectedStoreIds, period])

  const viewersTableFlexWidth = getViewersTableFlexWidth(tableSettings.draggableColumnsCount)

  const getDraggableTableColProps = (): TableColHeaderProps[] => {
    const columns: TableColHeaderProps[] = [
      {
        draggable: true,
        label: tableSettings.mainEntityColLabel,
        widthString: viewersTableFlexWidth,
        filterComponent: (
          <SelectTreeView
            {...getFilterPropsByColLabel(StatsTableColType.MAIN)}
          />
        ),
        style: {
          minWidth: viewersTableWidths.draggableColMinWidth,
          maxWidth: 'unset'
        }
      },
      {
        draggable: true,
        label: tableSettings.primaryCorrespondingEntityColLabel,
        widthString: viewersTableFlexWidth,
        filterComponent: (
          <SelectTreeView
            {...getFilterPropsByColLabel(StatsTableColType.PRIMARY)}
          />
        ),
        style: {
          minWidth: viewersTableWidths.draggableColMinWidth,
          maxWidth: 'unset'
        }
      }
    ]

    if (tableSettings.draggableColumnsCount > 2) {
      columns.push({
        draggable: true,
        label: tableSettings.secondaryCorrespondingEntityColLabel,
        widthString: viewersTableFlexWidth,
        filterComponent: (
          <SelectTreeView
            {...getFilterPropsByColLabel(StatsTableColType.SECONDARY)}
          />
        ),
        style: {
          minWidth: viewersTableWidths.draggableColMinWidth,
          maxWidth: 'unset'
        }
      })
    }

    return columns
  }

  const tableCols: TableColHeaderProps[] = [{
    label: selectorColLabel,
    widthPx: viewersTableWidths.selector,
    filterComponent: (
      <IconFilterEmpty
        style={{
          fill: styles.secondaryContent,
          marginLeft: -5
        }}
      />
    )
  },
  ...getDraggableTableColProps(),
  {
    label: StatsViewersTableSortProps.TOTAL_WATCH,
    widthPx: isExpandedWatchTimeColumn 
    ? viewersTableWidths.expandedTabs
    : viewersTableWidths.tabs,
      filterComponent: (
      <>
      {!isExpandedWatchTimeColumn 
      ? <IconPlusCircle onClick={() => setIsExpandedWatchTimeColumn(!isExpandedWatchTimeColumn)}/>
      : <IconMinusCircle onClick={() => setIsExpandedWatchTimeColumn(!isExpandedWatchTimeColumn)} />}
      </>),
    isColumnMarked: isColumnMarked(StatsViewersTableSortProps.TOTAL_WATCH),
    sortDirection: getSortDirection(StatsViewersTableSortProps.TOTAL_WATCH),
    setSortDirection: handleSortDirectionChange(StatsViewersTableSortProps.TOTAL_WATCH),
    disabled: isTotalWatchTimeColDisabled
  },
  {
    label: StatsViewersTableSortProps.TOTAL_INTERACTION,
    widthPx: isExpandedInteractionsColumn 
    ? viewersTableWidths.expandedTotalInteractionTime 
    : viewersTableWidths.totalInteractionTime,
      filterComponent: (
      <>
      {!isExpandedInteractionsColumn 
      ? <IconPlusCircle onClick={() => setIsExpandedInteractionsColumn(!isExpandedInteractionsColumn)}/>
      : <IconMinusCircle onClick={() => setIsExpandedInteractionsColumn(!isExpandedInteractionsColumn)} />}
      </>),
    isColumnMarked: isColumnMarked(StatsViewersTableSortProps.TOTAL_INTERACTION),
    sortDirection: getSortDirection(StatsViewersTableSortProps.TOTAL_INTERACTION),
    setSortDirection: handleSortDirectionChange(StatsViewersTableSortProps.TOTAL_INTERACTION)
  },
  {
    label: addExpensesColLabel,
    widthPx: viewersTableWidths.tabs,
    isColumnMarked: isColumnMarked(addExpensesColLabel),
    sortDirection: getSortDirection(addExpensesColLabel),
    setSortDirection: handleSortDirectionChange(addExpensesColLabel)
  },
  {
    label: StatsViewersTableSortProps.PURCHASE_REVENUE,
    widthPx: viewersTableWidths.tabs,
    isColumnMarked: isColumnMarked(StatsViewersTableSortProps.PURCHASE_REVENUE),
    sortDirection: getSortDirection(StatsViewersTableSortProps.PURCHASE_REVENUE),
    setSortDirection: handleSortDirectionChange(StatsViewersTableSortProps.PURCHASE_REVENUE)
  }]

  const tableBodyContainerRef = useRef<HTMLDivElement>(null)

  const [tableHeaderMarginLeft, setTableHeaderMarginLeft] = useState<number>(0)
  const [tableBodyScrollWidth, setTableBodyScrollWidth] = useState<number>(undefined)

  return (
    <div
      className={cl(styles.container, className)}
      style={style}
    >
      <Text2 style={{ color: styles.secondaryContent }}>
        *Select up to six {legendEntityLabel} to compare them in a graph.
                Hide columns to temporarily remove them from the graph.
                Drag a column in first position to filter by it.
      </Text2>

      <TableContainer className={styles.tableContainer}>
        <TableHeader
          tableColumns={tableCols}
          isExpandedInteractionsColumn={isExpandedInteractionsColumn}
          isExpandedWatchTimeColumn={isExpandedWatchTimeColumn}
          dragDropContextProps={{
            onDragEnd: onColumnDragEnd
          }}
          style={{
            marginLeft: tableHeaderMarginLeft,
            width: tableBodyScrollWidth,
            zIndex: 1
          }}
        />

        <TableHorizontalScrollControls
          containerRef={tableBodyContainerRef}
          onScrollLeft={(pixels) => setTableHeaderMarginLeft(-pixels)}
          onScrollWidthChange={(scrollWidth) => setTableBodyScrollWidth(scrollWidth)}
        />

        <TableBodyContainer
          ref={tableBodyContainerRef}
          className={styles.tableBodyContainer}
        >
          {processing
            ? (
              <LoadingSpinner
                show
                noBackground
                color={styles.primary}
                containerStyleProps={{
                  className: styles.loadingSpinner
                }}
              />
            )
            : searchResults.map(tableEntity => (
              <ViewersProjectsTableBodyLine
                key={tableEntity.mainEntity.id}
                tableEntity={tableEntity}
                style={{
                  width: tableBodyScrollWidth
                }}
              />
            ))}
        </TableBodyContainer>
        <PaginationButtons
          alwaysShow
          onPageSelect={onPageSelect}
          {...paginationInfo}
          style={{
            padding: '18px 0 22px'
          }}
        />
      </TableContainer>

    </div>
  )
}

const ViewersTable = inject(STATISTICS_VIEWERS_TABLE_STORE, STATISTICS_VIEWER_STORE, STATISTICS_PAGE_STORE
)(observer(ViewersTableComponent))
export { ViewersTable }
